import { discoveryCreators, type DiscoveryCreator, type DiscoveryOffer } from '../data/discovery';

export interface DiscoverySelection {
  creatorId: string;
  status: 'pending' | 'approved' | 'rejected';
  featured: boolean;
  order: number;
  offerId: string;
}

export const DISCOVERY_STORAGE_KEY = 'solra.discovery.workshop.v1';
export const DISCOVERY_CHANGE_EVENT = 'solra-discovery-workshop-change';

export function isDiscoveryWorkshopHost(hostname: string, productionEnabled = false): boolean {
  const productionHost = ['www.solra.app', 'solra.app',
    'solra.netlify.app', 'main--solra.netlify.app'].includes(hostname);
  const testHost = ['localhost', '127.0.0.1', '[::1]',
    'staging--solra.netlify.app'].includes(hostname)
    || /^deploy-preview-\d+--solra\.netlify\.app$/.test(hostname)
    || /^codex-discovery-workshop(?:-[a-z0-9-]+)?--solra\.netlify\.app$/.test(hostname);
  return testHost || (productionHost && productionEnabled);
}

export function initialDiscoverySelections(): DiscoverySelection[] {
  return discoveryCreators.map((creator, index) => ({
    creatorId: creator.id, status: index < 4 || creator.kind === 'collective' ? 'approved' : 'pending',
    featured: index < 2 || creator.kind === 'collective', order: index + 1, offerId: creator.offers[0].id,
  }));
}

export function parseDiscoverySelections(raw: string | null): DiscoverySelection[] {
  const defaults = initialDiscoverySelections();
  if (!raw) return defaults;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return defaults;
    return defaults.map((fallback) => {
      const row = parsed.find((value: unknown) => value && typeof value === 'object'
        && 'creatorId' in value && value.creatorId === fallback.creatorId);
      const creator = discoveryCreators.find((value) => value.id === fallback.creatorId);
      if (!row || !creator || typeof row.featured !== 'boolean'
        || !['pending', 'approved', 'rejected'].includes(row.status)
        || !Number.isInteger(row.order) || row.order < 1 || row.order > 999
        || !creator.offers.some((offer) => offer.id === row.offerId)) return fallback;
      // Whitelist curation fields. Stored content can never inject names, links, or HTML.
      return { creatorId: fallback.creatorId, status: row.status,
        featured: row.featured, order: row.order, offerId: row.offerId };
    });
  } catch {
    return defaults;
  }
}

export function selectedPublishedOffer(creator: DiscoveryCreator, selection: DiscoverySelection): DiscoveryOffer | undefined {
  return creator.offers.find((offer) => offer.id === selection.offerId && offer.status === 'published');
}

export function visibleDiscoveryCreators(selections: DiscoverySelection[], creators: DiscoveryCreator[] = discoveryCreators) {
  return creators.flatMap((creator) => {
    const selection = selections.find((row) => row.creatorId === creator.id);
    if (!selection || selection.status !== 'approved') return [];
    const offer = selectedPublishedOffer(creator, selection);
    return offer ? [{ creator, selection, offer }] : [];
  }).sort((a, b) => a.selection.order - b.selection.order || a.creator.name.localeCompare(b.creator.name));
}

// Editorial Discovery introduces selected creators directly. Their storefront
// remains the destination, so a product or event is not required for a slot.
export function visibleEditorialCreators(selections: DiscoverySelection[], creators: DiscoveryCreator[]) {
  return creators.flatMap((creator) => {
    if (creator.kind !== 'creator') return [];
    const selection = selections.find((row) => row.creatorId === creator.id);
    return selection?.status === 'approved' ? [{ creator, selection }] : [];
  }).sort((a, b) => a.selection.order - b.selection.order || a.creator.name.localeCompare(b.creator.name));
}
